// Deterministic project/group/member fixtures that layer on top of the
// existing service catalog. Each project owns a slice of services (by team
// affiliation) so the numbers on the tiles feel real.

import type {
  Project, ProjectGroup, ProjectMember, ProjectStatus,
  Service, Environment, ProjectGroupKind,
} from "@coremonit/types";
import { makeRng } from "./rng";

// The static project catalog — teams roughly aligned with the service.team
// values in `services.ts`. Keep the count small enough that every project
// tile fits on one dashboard row and large enough to feel real.
const PROJECT_CATALOG: Array<{
  key: string; name: string; description: string; color: string; icon: string;
  ownerEmail: string; teams: string[];
}> = [
  { key: "storefront",   name: "Storefront",             description: "Public-facing e-commerce experience and mobile clients",       color: "#2D7FF9", icon: "ShoppingCart", ownerEmail: "alice.wong@coremonit.io",   teams: ["web-platform", "mobile"] },
  { key: "commerce",     name: "Commerce Platform",      description: "Orders, inventory, search, billing — the transactional core", color: "#10B981", icon: "Package",      ownerEmail: "bob.chen@coremonit.io",     teams: ["commerce", "billing", "search"] },
  { key: "payments",     name: "Payments & Fraud",       description: "Payments processing, PCI-scoped services and 3-DS callbacks",  color: "#F59E0B", icon: "CreditCard",   ownerEmail: "carol.silva@coremonit.io",  teams: ["payments"] },
  { key: "identity",     name: "Identity & Access",      description: "Auth, session, roles, SSO federation, MFA",                    color: "#A855F7", icon: "ShieldCheck",  ownerEmail: "diego.perez@coremonit.io",  teams: ["identity"] },
  { key: "data-and-ml",  name: "Data & ML",              description: "Reporting pipelines and recommendation models",                color: "#06B6D4", icon: "Brain",        ownerEmail: "erin.zhao@coremonit.io",    teams: ["data", "ml"] },
  { key: "infra",        name: "Infrastructure",         description: "Shared platform services — queues, caches, databases, gateway", color: "#EF4444", icon: "Server",       ownerEmail: "farida.ahmed@coremonit.io", teams: ["platform", "dbre"] },
];

const EXTRA_MEMBERS = [
  { email: "gabe.martins@coremonit.io",   name: "Gabe Martins",   role: "editor" as const },
  { email: "hana.tan@coremonit.io",       name: "Hana Tan",       role: "editor" as const },
  { email: "ivan.novak@coremonit.io",     name: "Ivan Novak",     role: "viewer" as const },
  { email: "jia.li@coremonit.io",         name: "Jia Li",         role: "viewer" as const },
  { email: "karim.hassan@coremonit.io",   name: "Karim Hassan",   role: "editor" as const },
  { email: "lin.zhang@coremonit.io",      name: "Lin Zhang",      role: "viewer" as const },
];

/** Bucket the fleet into projects by team affiliation. Services whose team
 *  isn't claimed fall into `infra` so nothing is orphaned. */
export function assignServicesToProjects(services: Service[]): Map<string, string> {
  const byService = new Map<string, string>(); // serviceId → projectId
  for (const svc of services) {
    const owner = PROJECT_CATALOG.find((p) => p.teams.includes(svc.team));
    byService.set(svc.id, owner ? `proj_${owner.key}` : "proj_infra");
  }
  return byService;
}

function rollupStatus(services: Service[]): ProjectStatus {
  if (!services.length) return "unknown";
  if (services.some((s) => s.status === "critical")) return "critical";
  if (services.some((s) => s.status === "warning"))  return "warning";
  if (services.every((s) => s.status === "unknown")) return "unknown";
  return "healthy";
}

function envsOf(services: Service[]): Environment[] {
  return [...new Set(services.map((s) => s.environment))];
}

export interface ProjectBundle {
  projects: Project[];
  groups:   ProjectGroup[];
  members:  ProjectMember[];
  /** Also returned so callers can persist the projectId back onto Service. */
  serviceToProject: Map<string, string>;
}

export function generateProjects(services: Service[], seed = 84): ProjectBundle {
  const rng = makeRng(seed);
  const serviceToProject = assignServicesToProjects(services);

  const projects: Project[] = PROJECT_CATALOG.map((p) => {
    const id = `proj_${p.key}`;
    const owned = services.filter((s) => serviceToProject.get(s.id) === id);
    const envs = envsOf(owned);
    // Reasonable rollups drawn from owned services — never fake numbers when
    // we can compute the real thing from the fleet.
    const avg  = <T>(xs: T[], f: (t: T) => number) => xs.length ? xs.reduce((a, b) => a + f(b), 0) / xs.length : 0;
    return {
      id,
      key: p.key,
      name: p.name,
      description: p.description,
      color: p.color,
      icon: p.icon,
      status: rollupStatus(owned),
      environments: envs,
      ownerEmail: p.ownerEmail,
      createdAt: "2025-09-01T09:00:00Z",
      updatedAt: new Date().toISOString(),
      serviceCount:  owned.length,
      groupCount:    3 + Math.floor(rng() * 2),                       // computed properly below
      memberCount:   1 + 2 + Math.floor(rng() * 4),
      incidentCount: owned.filter((s) => s.status !== "healthy").length,
      metrics: {
        availabilityPct:      Math.round(avg(owned, (s) => s.availabilityPct) * 100) / 100 || 100,
        latencyP95Ms:         Math.round(avg(owned, (s) => s.latencyP95Ms)),
        errorRatePct:         Math.round(avg(owned, (s) => s.errorRatePct) * 100) / 100,
        requestsPerMin:       Math.round(avg(owned, (s) => s.requestsPerMin)),
        deploymentSuccessPct: 92 + Math.round(rng() * 700) / 100,
      },
    };
  });

  // Groups — every project gets:
  //   1. an env-based "Production" group (if it has prod services)
  //   2. an env-based "Staging + dev" group
  //   3. a provider-based "AWS (EKS)" group (if any EKS/ECS service present)
  // …plus provider groups for AKS/GKE when we see them.
  const groups: ProjectGroup[] = [];
  for (const project of projects) {
    const owned = services.filter((s) => serviceToProject.get(s.id) === project.id);
    const pushGroup = (
      partial: Omit<ProjectGroup, "id" | "projectId" | "serviceCount" | "createdAt">,
      matcher: (s: Service) => boolean,
    ) => {
      const count = owned.filter(matcher).length;
      if (!count) return;
      groups.push({
        id: `grp_${project.key}_${partial.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
        projectId: project.id,
        serviceCount: count,
        createdAt: "2025-09-02T09:00:00Z",
        ...partial,
      });
    };

    pushGroup(
      { name: "Production",          description: "Live production services", kind: "environment", targets: ["production"], color: "#EF4444" },
      (s) => s.environment === "production",
    );
    pushGroup(
      { name: "Staging & Dev",       description: "Non-prod environments",    kind: "environment", targets: ["staging", "development", "uat"], color: "#F59E0B" },
      (s) => s.environment !== "production",
    );
    pushGroup(
      { name: "AWS (EKS + ECS)",     description: "AWS-hosted deployments",   kind: "provider",    targets: ["eks", "ecs"], color: "#F59E0B" },
      (s) => s.deployment.type === "eks" || s.deployment.type === "ecs",
    );
    pushGroup(
      { name: "Azure (AKS)",         description: "Azure-hosted deployments", kind: "provider",    targets: ["aks"],        color: "#2D7FF9" },
      (s) => s.deployment.type === "aks",
    );
    pushGroup(
      { name: "Google Cloud (GKE)",  description: "GCP-hosted deployments",   kind: "provider",    targets: ["gke"],        color: "#10B981" },
      (s) => s.deployment.type === "gke",
    );
  }

  // Members — the project owner as project_admin, plus 2–4 rotating members.
  const members: ProjectMember[] = [];
  const emailToName = (email: string) => email.split("@")[0].split(".").map((w) => w[0].toUpperCase() + w.slice(1)).join(" ");
  for (const project of projects) {
    const owner = PROJECT_CATALOG.find((p) => p.key === project.key)!;
    members.push({
      userId: `usr_${owner.ownerEmail.split("@")[0].replace(/\./g, "-")}`,
      projectId: project.id,
      email: owner.ownerEmail,
      name: emailToName(owner.ownerEmail),
      role: "project_admin",
      addedAt: "2025-09-01T09:00:00Z",
    });
    // Sprinkle 2-4 additional members deterministically.
    const extraCount = 2 + Math.floor(rng() * 3);
    for (let i = 0; i < extraCount; i++) {
      const idx = (Math.floor(rng() * 10000)) % EXTRA_MEMBERS.length;
      const m = EXTRA_MEMBERS[idx];
      if (members.some((x) => x.projectId === project.id && x.email === m.email)) continue;
      members.push({
        userId: `usr_${m.email.split("@")[0].replace(/\./g, "-")}`,
        projectId: project.id,
        email: m.email,
        name: m.name,
        role: m.role,
        addedAt: "2025-09-05T12:00:00Z",
        addedBy: owner.ownerEmail,
      });
    }
  }

  // Fix up groupCount and memberCount now that we know the truth.
  for (const project of projects) {
    project.groupCount  = groups.filter((g) => g.projectId === project.id).length;
    project.memberCount = members.filter((m) => m.projectId === project.id).length;
  }

  return { projects, groups, members, serviceToProject };
}
